'use client';

import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

export type TooltipProviderElement = React.ComponentRef<
  typeof TooltipPrimitive.Provider
>;
export type TooltipProviderProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Provider
>;
function TooltipProvider({
  delayDuration = 0,
  ...props
}: TooltipProviderProps): React.JSX.Element {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

export type TooltipElement = React.ComponentRef<typeof TooltipPrimitive.Root>;
export type TooltipProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
>;
function Tooltip(props: TooltipProps): React.JSX.Element {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        {...props}
      />
    </TooltipProvider>
  );
}

export type TooltipTriggerElement = React.ComponentRef<
  typeof TooltipPrimitive.Trigger
>;
export type TooltipTriggerProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Trigger
>;
function TooltipTrigger(props: TooltipTriggerProps): React.JSX.Element {
  return (
    <TooltipPrimitive.Trigger
      {...props}
    />
  );
}

export type TooltipContentElement = React.ComponentRef<
  typeof TooltipPrimitive.Content
>;
export type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
>;
function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: TooltipContentProps): React.JSX.Element {
  return (
    <TooltipPrimitive.Portal>
      {/*
        Calm tooltip default (applied globally across the dashboard +
        marketing apps via every consumer of @workspace/ui/components/tooltip):

        - bg-popover / text-popover-foreground + border + shadow-sm: light
          popover surface instead of the original heavy black primary fill,
          which read as visually loud and clashed with the rest of the UI.
        - pointer-events-none: the tooltip cannot be hovered. Fixes the
          long-standing "tooltip stays open when the cursor drifts onto it"
          bug — Radix's default keeps content hoverable so users can interact
          with tooltip content, but our tooltips are pure text so we don't
          need that affordance. Mouse leaves trigger → tooltip closes.
        - No Arrow primitive: a rotated-square arrow on a bordered light
          surface looks janky (the border doesn't follow the rotation and
          you get a visible seam). Linear/Vercel-style: no arrow, just a
          small sideOffset so the tooltip floats off the trigger.
        - max-w-[260px] + text-balance: prevents single-word last-lines.
      */}
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'pointer-events-none z-50 w-fit max-w-[260px] origin-(--radix-tooltip-content-transform-origin)',
          'rounded-md border border-border/70 bg-popover px-2.5 py-1.5 text-[11.5px] leading-snug text-popover-foreground text-balance shadow-sm',
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
